import { useContractQuery } from './useContractQuery';

/* -------------------------------------------------------------- *
 *   Per-request data (one-request granularity).
 *   For milestone-keyed data on a request, see useMilestones.ts.
 * -------------------------------------------------------------- */

/**
 * Auto-getter for `requestList(id)` — scalar fields of the Request struct.
 * Dynamic arrays (milestone names/amounts, auditors[]) and mappings are
 * stripped by the auto getter; use the dedicated helpers for those.
 *
 * v3 adds optionExerciseRequired (BARDA option-period gating).
 */
export type RequestRow = {
  labWallet: string;
  title: string;
  description: string;
  ipfsHash: string;
  currentMilestone: bigint;
  hype: bigint;
  verdictCast: boolean;
  verdictAccepted: boolean;
  cancelled: boolean;
  optionExerciseRequired: boolean;
  approveVotes: bigint;
  totalVotes: bigint;
  hypeSnapshotId: bigint;
};

export function useRequest(id: number | bigint | null | undefined) {
  return useContractQuery<RequestRow>(
    ['auditingPool', 'requestList', BigInt(id ?? 0)],
    { enabled: id !== null && id !== undefined }
  );
}

/** Canonical helper getRequestStatus() — same data, cleaner tuple. */
export function useRequestStatus(id: number | bigint | null | undefined) {
  type Status = {
    verdictCast: boolean;
    verdictAccepted: boolean;
    cancelled: boolean;
    currentMilestone: bigint;
  };
  return useContractQuery<Status>(
    ['auditingPool', 'getRequestStatus', BigInt(id ?? 0)],
    { enabled: id !== null && id !== undefined }
  );
}

/** v3 — has any ClinicalData milestone? Drives DSMB veto UI exposure. */
export function useRequestHasClinical(id: number | bigint | null | undefined) {
  return useContractQuery<boolean>(
    ['auditingPool', 'requestHasClinical', BigInt(id ?? 0)],
    { enabled: id !== null && id !== undefined }
  );
}

/** Lab address that owns the request. */
export function useRequestWallet(id: number | bigint | null | undefined) {
  return useContractQuery<string>(
    ['auditingPool', 'getRequestWallet', BigInt(id ?? 0)],
    { enabled: id !== null && id !== undefined }
  );
}

/** Lab's outstanding 0.005 ETH submission deposit (per address). */
export function useRequestDepositOf(address: string | null | undefined) {
  return useContractQuery<bigint>(
    ['auditingPool', 'requestDeposits', address ?? '0x0'],
    { enabled: !!address }
  );
}

/** Auditors[] for a request — addresses that applied. */
export function useRequestAuditors(id: number | bigint | null | undefined) {
  return useContractQuery<string[]>(
    ['auditingPool', 'getRequestAuditors', BigInt(id ?? 0)],
    { enabled: id !== null && id !== undefined }
  );
}
